import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { DoraButton, DoraSelectMultiple, DoraSelect, DoraDatePicker } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../../redux-store";
import { useSelector } from "react-redux";
import { loadDrivers } from "../../../../ducks/data/drivers";
import { selectDrivers } from "../../../../ducks/data/drivers/selectors";
import {
  startCopyCargoToDriverRoutes,
  fetchCopyCargoToDriverRoutesJob,
} from "../../../../ducks/app/cargo-dialog";
import { notifyL } from "../../../../ducks/notifications";
import { notify } from "../../../../ducks/notifications";
import { DateTime } from "luxon";
import { selectDefaultCopyOption } from "../../../../ducks/data/user-settings/selectors";
import { DefaultCopyOption } from "dora-contracts";
import { useConfirmDropDeletedExtraCo2Types } from "../../ConfirmDropDeletedExtraCo2Types";

type InvoiceMode = "NONE" | "WITH" | "WITH_RESET";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 180_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// The selector defaults to the invoice handling implied by the user's default
// copy option (reset quantities when the default is itself "copy to routes").
const invoiceModeFromCopyOption = (option: DefaultCopyOption): InvoiceMode => {
  switch (option) {
    case "COPY_ORDER":
      return "NONE";
    case "COPY_WITH_INVOICE_LINES":
      return "WITH";
    default:
      return "WITH_RESET";
  }
};

const CopyCargoToDriverRoutesDialog = ({
  onClose,
  cargoId,
}: {
  onClose: () => void;
  cargoId: string;
}) => {
  const { t } = useTranslation(["components", "shipmentModal"]);
  const dispatch = useAppDispatch();
  const {
    ask: askToDropDeletedExtraCo2Types,
    dialog: dropDeletedExtraCo2TypesDialog,
  } = useConfirmDropDeletedExtraCo2Types("COPY");
  const drivers = useSelector(selectDrivers);
  const defaultCopyOption = useSelector(selectDefaultCopyOption);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  // Defaults to today — the day the user chooses to copy.
  const [date, setDate] = useState<DateTime>(DateTime.now());
  const [invoiceMode, setInvoiceMode] = useState<InvoiceMode>(() =>
    invoiceModeFromCopyOption(defaultCopyOption)
  );

  useEffect(() => {
    dispatch(loadDrivers());
  }, [dispatch]);

  const options = useMemo(
    () =>
      drivers
        .filter((d) => d.active)
        .map((d) => ({
          id: d.id,
          label: `${d.number} - ${d.firstName} ${d.lastName}`,
        })),
    [drivers]
  );

  const selectedValues = useMemo(
    () => options.filter((o) => selectedDriverIds.includes(o.id)),
    [options, selectedDriverIds]
  );

  const submitDisabled =
    requestInProgress || selectedDriverIds.length === 0 || !date?.isValid;

  const invoiceModeOptions = useMemo(
    () => [
      {
        value: "NONE",
        label: t("shipmentModal:copyToDriverRoutes.invoiceModeNone"),
      },
      {
        value: "WITH",
        label: t("shipmentModal:copyToDriverRoutes.invoiceModeWith"),
      },
      {
        value: "WITH_RESET",
        label: t("shipmentModal:copyToDriverRoutes.invoiceModeWithReset"),
      },
    ],
    [t]
  );

  const onCopyClick = async () => {
    if (selectedDriverIds.length === 0 || !date?.isValid) {
      return;
    }
    setRequestInProgress(true);
    setProgress({ done: 0, total: selectedDriverIds.length });
    try {
      const start = (dropDeletedExtraCo2Types: boolean) =>
        dispatch(
          startCopyCargoToDriverRoutes({
            cargoId,
            driverIds: selectedDriverIds,
            date: date.toISODate(),
            copyInvoiceLines: invoiceMode !== "NONE",
            resetInvoiceLineQuantities: invoiceMode === "WITH_RESET",
            dropDeletedExtraCo2Types,
          })
        ).unwrap();
      let started = await start(false);
      if ("refused" in started) {
        if (!(await askToDropDeletedExtraCo2Types())) {
          return;
        }
        started = await start(true);
        if ("refused" in started) {
          return;
        }
      }
      const { jobId } = started;

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // Poll the job until it completes, fails, or times out.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const job = await dispatch(
          fetchCopyCargoToDriverRoutesJob(jobId)
        ).unwrap();
        setProgress({ done: job.results.length, total: job.total });
        if (job.status === "COMPLETED") {
          dispatch(
            notifyL({
              namespace: "shipmentModal",
              key: "copyToDriverRoutes.success",
              type: "success",
            })
          );
          onClose();
          return;
        }
        if (job.status === "FAILED") {
          dispatch(
            notify({
              message: job.error ?? "Copy failed",
              type: "error",
            })
          );
          return;
        }
        if (Date.now() > deadline) {
          dispatch(
            notifyL({
              namespace: "shipmentModal",
              key: "copyToDriverRoutes.timeout",
              type: "warning",
            })
          );
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    } catch {
      // The startCopyCargoToDriverRoutes / fetchCopyCargoToDriverRoutesJob thunks have already
      // reported it and shown the error toast.
    } finally {
      setRequestInProgress(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm">
      {dropDeletedExtraCo2TypesDialog}
      <DialogTitle>{t("shipmentModal:copyToDriverRoutes.title")}</DialogTitle>
      {requestInProgress ? (
        <DialogContent>
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <CircularProgress size={32} />
            <span>
              {t("shipmentModal:copyToDriverRoutes.progress", {
                done: progress?.done ?? 0,
                total: progress?.total ?? selectedDriverIds.length,
              })}
            </span>
          </div>
        </DialogContent>
      ) : (
      <DialogContent>
        <div>{t("shipmentModal:copyToDriverRoutes.text")}</div>
        <div className="mt-4">
          <div>{t("shipmentModal:copyToDriverRoutes.dateLabel")}</div>
          <div className="mt-2">
            <DoraDatePicker
              id="copy-to-driver-routes-date"
              value={date}
              onChange={(d) => d && setDate(d)}
            />
          </div>
        </div>
        <div className="mt-4">
          <div>{t("shipmentModal:copyToDriverRoutes.driversLabel")}</div>
          <div className="mt-2">
            <DoraSelectMultiple
              className="w-full"
              options={options}
              selectedValues={selectedValues}
              onChange={(ids: string[]) => setSelectedDriverIds(ids)}
              placeholder={t(
                "shipmentModal:copyToDriverRoutes.driversPlaceholder"
              )}
            />
          </div>
        </div>
        <div className="mt-4">
          <div>{t("shipmentModal:copyToDriverRoutes.invoiceModeLabel")}</div>
          <div className="mt-2">
            <DoraSelect
              className="w-full"
              fullWidth
              options={invoiceModeOptions}
              value={invoiceMode}
              onChange={(v) => v && setInvoiceMode(v as InvoiceMode)}
            />
          </div>
        </div>
      </DialogContent>
      )}
      <DialogActions>
        <div className="flex gap-2 items-center">
          <DoraButton
            variant="primary"
            onClick={onClose}
            disabled={requestInProgress}
          >
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            onClick={onCopyClick}
            disabled={submitDisabled}
          >
            {t("shipmentModal:copyToDriverRoutes.submit")}
          </DoraButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CopyCargoToDriverRoutesDialog;
